(ns frontend.view.user-management.insider-groups
    (:require
      [clojure.string :as string]
      [common.entity :as entity]
      [common.localization :as loc]
      [common.util :as cu]
      [frontend.state.current-session :as session]
      [frontend.ux-overhaul.table.view :as table]
      [frontend.view.user-management.components :as components]
      [frontend.view.user-management.events :as events]
      [oops.core :as oops]
      [reagent.core :as r]
      [ui.components.select.select :as select]))

(defn insider-groups-renderer
  [groups]
  (let [group-count (count groups)]
    (cond
      (zero? group-count)
      "-"
      (> group-count 5)
      (str (->> groups
                (take 3)
                (map entity/display-string)
                (string/join ", "))
           " and "
           (- group-count 3)
           " other insider groups")
      :else
      (->> groups
           (map entity/display-string)
           (string/join ", ")))))

(defn team-renderer
  [teams]
  (let [teams (->> teams
                   (map (fn [t]
                          (if (:db/id t)
                            t
                            (session/get-entity t)))))
        team-count (count teams)]
    (cond
      (zero? team-count)
      "-"
      (> team-count 5)
      (str (->> teams
                (map entity/display-string)
                (sort)
                (take 3)
                (string/join ", "))
           " and "
           (- team-count 3)
           " other teams")
      :else
      (->> teams
           (map entity/display-string)
           (sort)
           (string/join ", ")))))

(defn team-member-renderer
  [members]
  (let [member-count (count members)]
    (cond
      (zero? member-count)
      "-"
      (> member-count 5)
      (str (->> members
                (take 3)
                (map (fn [id]
                       (entity/display-string (session/get-entity id))))
                (string/join ", "))
           " and "
           (- member-count 3)
           " other members")
      :else
      (->> members
           (map (fn [id]
                  (entity/display-string (session/get-entity id))))
           (sort)
           (string/join ", ")))))

(defn resolve-columns
  []
  (concat
    [{:headerName (loc/tr :insider-group/name)
      :sortable   true
      :field      (clj->js :insider-group/name)}
     {:headerName (loc/tr :insider-group/status)
      :sortable   true
      :field      (clj->js :insider-group/status)}
     {:headerName   (loc/tr :insider-group/members)
      :sortable     true
      :field        (clj->js :insider-group/members)
      :cellRenderer (fn [props]
                      (team-member-renderer (events/get-cell-value props)))}
     {:headerName   (loc/tr :insider-group/teams)
      :sortable     true
      :field        (clj->js :insider-group/teams)
      :cellRenderer (fn [props]
                      (team-renderer (events/get-cell-value props)))}]))

(defn resolve-user-columns
  []
  [{:headerName (loc/tr :account/email)
    :sortable   true
    :resizable  true
    :field      :account/email}
   {:headerName (loc/tr :account/first-name)
    :sortable   true
    :field      :account/first-name}
   {:headerName (loc/tr :account/last-name)
    :sortable   true
    :field      :account/last-name}
   {:headerName (loc/tr :direct-insider-groups)
    :sortable   true
    :field      :direct-insider-groups}
   {:headerName (loc/tr :insider-teams)
    :sortable   true
    :field      :insider-teams}
   {:headerName (loc/tr :insider-groups)
    :sortable   true
    :field      :insider-groups}
   {:headerName   (loc/tr :admin-user)
    :sortable     true
    :field        :admin-user
    :cellRenderer (fn [props]
                    (let [value (events/get-cell-value props)]
                      (r/as-element
                        [:span
                         {:class "text-xl"}
                         (if value
                           [:i.fal.fa-check-circle]
                           [:i.fal.fa-times-circle])])))}])

(defn resolve-row-data
  ([insider-group] (resolve-row-data insider-group false))
  ([insider-group selected?]
   (merge insider-group
          {:row-selected? selected?})))

(defn resolve-user-row-data
  [insider-groups account selected?]
  (let [account-id (:db/id account)
        direct-insider-groups (->> insider-groups
                                   (filter (fn [ig]
                                             (some #{account-id}
                                                   (:insider-group/members ig))))
                                   (seq))
        insider-teams (->> insider-groups
                           (mapcat :insider-group/teams)
                           (map session/get-entity)
                           (filter (fn [team]
                                     (some #{account-id} (:team/members team)))))
        insider-groups (->> insider-groups
                            (filter (fn [ig]
                                      (or (some #{account-id} (:insider-group/members ig))
                                          (some #{account-id}
                                                (->> ig
                                                     :insider-group/teams
                                                     (map session/get-entity)
                                                     (mapcat :team/members)))))))]
    (merge account
           {:row-selected?         selected?
            :admin-user            (boolean (some #{account-id} @session/admin-role-account-ids))
            :direct-insider-groups (insider-groups-renderer direct-insider-groups)
            :insider-teams         (team-renderer insider-teams)
            :insider-groups        (insider-groups-renderer insider-groups)})))

(defn groups-table
  [_resolved-groups _state]
  (let [groups-table-state (r/atom {})]
    (fn [resolved-groups state]
      (let [column-defs (resolve-columns)
            on-selection-changed (fn [params]
                                   (let [grid-api (aget params "api")
                                         selected-nodes-js (oops/ocall grid-api "getSelectedNodes")
                                         selected-nodes (js->clj selected-nodes-js :keywordize-keys true)
                                         _ (js/console.log "SelectedNodes : " selected-nodes)
                                         selected-node (first selected-nodes)]
                                     (if selected-node
                                       (let [selected-node-data (aget selected-node "data")
                                             selected-node-id (:id (js->clj selected-node-data {:keywordize-keys true}))]
                                         (swap! state assoc :selected-group-id selected-node-id))
                                       (swap! state dissoc :selected-group-id))))]
        (if-not resolved-groups
          [components/not-found-view]
          [:div
           [table/view
            {:grid-id              "insider-groups-table"
             :state-atom           groups-table-state
             :column-defs          column-defs
             :row-data             resolved-groups
             :default-col-def      {:sortable true
                                    :editable false
                                    :menuTabs ["generalMenuTab"]}
             :auto-height?         true
             :min-height           "300px"
             :width                "100%"
             :row-selection        {:mode "singleRow"}
             :on-selection-changed on-selection-changed
             :tools                [:div
                                    {:class "py-2 flex gap-3 text-base-light-text bg-base-light"}
                                    [components/insider-tools state groups-table-state]]}]])))))

(defn users-table
  [resolved-users]
  (let [column-defs (resolve-user-columns)
        users-table-state (r/atom {})]
    (if-not resolved-users
      [components/not-found-view]
      [:<>
       [table/view
        {:grid-id         "insider-groups-users-table"
         :state-atom      users-table-state
         :column-defs     column-defs
         :row-data        resolved-users
         :default-col-def {:sortable true
                           :editable false
                           :menuTabs ["generalMenuTab"]}
         :row-selection   {:mode "singleRow"}
         :min-height      "300px"
         :auto-height?    true
         :width           "100%"}]])))

(defn view
  [state]
  (let [update-search-term! (fn [value]
                              (swap! state assoc :insider-search value))]
    (swap! state assoc :selected-tab :settings/insider-groups)
    (fn [state]
      (let [insider-groups (->> @session/all-insider-groups
                                (remove :common/disabled?))
            resolved-groups (->> insider-groups
                                 (events/filter-insiders-by-search state (:insider-search @state))
                                 (map resolve-row-data)
                                 (seq))
            selected-insider-group (:selected-insider-group @state)
            selected-insider-groups (->> insider-groups
                                         (filter (fn [ig]
                                                   (or (not (number? selected-insider-group))
                                                       (= (:db/id ig) selected-insider-group))))
                                         (seq))
            insider-user-ids (set (concat (->> selected-insider-groups
                                               (mapcat :insider-group/members))
                                          (->> selected-insider-groups
                                               (mapcat :insider-group/teams)
                                               (set)
                                               (mapcat (fn [team-id]
                                                         (let [team (session/get-entity team-id)]
                                                           (:team/members team))))
                                               (set))
                                          (->> @session/admin-role-account-ids)))
            resolved-users (->> insider-user-ids
                                (map session/get-entity)
                                (map (partial resolve-user-row-data selected-insider-groups))
                                (seq))
            options (->> insider-groups
                         (map (fn [ig]
                                {:id    (:db/id ig)
                                 :value ig
                                 :label (entity/display-string ig)}))
                         (sort-by :label))]
        [:div
         {:class "p-4 flex flex-col gap-4"}
         [:div
          {:class "flex flex-col gap-4"}
          [:div
           {:class "flex items-start gap-2 text-base-light-text-paler"}
           [:i
            {:class "pt-1 fas fa-info-circle"}]
           [:span (loc/tr :settings/insider-group-notification)]]
          [:div
           {:class "flex items-center gap-4"}
           [:div
            {:class "min-w-72 relative"}
            [components/search-bar (:insider-search @state) update-search-term! state]
            [:i
             {:class "fal fa-search absolute top-2/4 -translate-y-2/4 right-3"}]]]
          [groups-table resolved-groups state]]

         [:div
          {:class "flex flex-col gap-4"}
          [:span
           {:class "text-xl font-bold"}
           (loc/tr :insider-group-accounts-heading)]

          [:div
           [:div
            {:class "flex flex-col gap-2 max-w-72 relative"}
            [:label
             {:class ""}
             (loc/tr :show-insider-group-accounts)]
            [select/view
             {:value       (->> options
                                (cu/filter-by :value selected-insider-group)
                                (first))
              :placeholder "All insider groups"
              :on-change   (fn [k]
                             (swap! state assoc :selected-insider-group k)
                             (deref state))
              :options     options}]]]

          [users-table resolved-users state]]]))))

